import { Box, InputAdornment, Typography } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import EditIcon from '@material-ui/icons/Edit'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import AlertDialog, { AlertDialogProps } from '@/components/alert-dialog'
import Button from '@/components/button'
import { useRevokeIntegration } from '@/hooks/mutations/use-revoke-integration'
import { useGetUrlIntegration } from '@/hooks/queries/use-get-url-integration'
import { useGetUserIntegrations } from '@/hooks/queries/use-get-user-integrations'
import { useAuth } from '@/hooks/use-auth'
import { routes } from '@/routes/config'

import InputText from '../../components/input-text'
import ChangePassword from './change-password'
import { Layout } from './layout'
import { useStyles } from './styles'

const JOHN_DEERE_INTEGRATION_KEY = 'john_deere_integration'

const integrations = [
  {
    name: 'john-deere',
    img: { src: 'assets/img/john_deere_2.svg', alt: 'Ícone John Deere' },
  },
]

export default function EditAccount() {
  const classes = useStyles()
  const { user } = useAuth()
  const [isChangePassword, setIsChangePassword] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const userIntegrationsQuery = useGetUserIntegrations()
  const urlIntegrationQuery = useGetUrlIntegration({
    enabled:
      userIntegrationsQuery.isFetched && !checkIntegrationActive('john-deere'),
  })
  const revokeIntegrationMutation = useRevokeIntegration()

  function checkIntegrationActive(name: string) {
    return !!userIntegrationsQuery.data?.find(
      (integration) => name === integration.name,
    )?.active
  }

  function removeJohnDeereIntegrationParam() {
    setSearchParams((prev) => {
      prev.delete(JOHN_DEERE_INTEGRATION_KEY)
      return prev
    })
  }

  function getModalProps(): AlertDialogProps {
    const openModal = !!searchParams.get(JOHN_DEERE_INTEGRATION_KEY)

    const hasJohnDeereIntegration = searchParams.get(JOHN_DEERE_INTEGRATION_KEY)

    if (!hasJohnDeereIntegration)
      return {
        open: false,
        icon: <></>,
        variant: 'success',
        title: '',
        subtitle: '',
        confirm: '',
      }

    if (hasJohnDeereIntegration === 'true') {
      return {
        open: openModal,
        icon: <CheckCircleOutlineIcon />,
        variant: 'success',
        title: 'Integração finalizada',
        subtitle:
          'A integração com o John Deere Operations Center foi realizada com sucesso.',
        confirm: 'Concluir',
        handleConfirm: removeJohnDeereIntegrationParam,
      }
    }

    return {
      open: openModal,
      icon: <ReportProblemOutlinedIcon />,
      variant: 'error',
      title: 'Integração não realizada',
      subtitle:
        'Não foi possível concluir a integração com a John Deere Operations Center, por favor tente novamente mais tarde.',
      confirm: 'Concluir',
      handleConfirm: removeJohnDeereIntegrationParam,
    }
  }

  return (
    <Layout>
      <Typography variant={'h3'}>Informações básicas</Typography>
      <Box className={classes.wrapper}>
        <Typography variant={'h4'}>
          As mudanças feitas no seu nome irão afetar toda a sua conta cromai
        </Typography>
        <InputText
          aria-readonly
          role='button'
          value={user.name}
          label='Nome'
          onClick={() => navigate(routes.EditName())}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <EditIcon color='secondary' />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Typography variant={'h3'}>Segurança</Typography>
      <Box className={classes.wrapper}>
        <Typography variant={'h4'}>
          Mantenha esta informação atualizada para não perder o acesso a sua
          conta cromai
        </Typography>
        <InputText
          aria-readonly
          role='button'
          value='********'
          label='Senha'
          onClick={() => {
            setIsChangePassword(true)
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <EditIcon color='secondary' />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <ChangePassword
        open={isChangePassword}
        onClose={() => {
          setIsChangePassword(false)
        }}
      />

      <Typography variant={'h3'}>Extensões</Typography>
      <Box className={classes.wrapperCards}>
        {integrations.map((integration) => (
          <Box className={classes.card} key={integration.name}>
            <img src={integration.img.src} alt={integration.img.alt}></img>
            {checkIntegrationActive(integration.name) ? (
              <Button
                className={classes.cardButton}
                loading={
                  revokeIntegrationMutation.isLoading ||
                  userIntegrationsQuery.isFetching
                }
                outlined
                onClick={() =>
                  revokeIntegrationMutation.mutate(undefined, {
                    onSuccess: () => userIntegrationsQuery.refetch(),
                  })
                }
              >
                Desabilitar
              </Button>
            ) : (
              <Link
                to={
                  urlIntegrationQuery.data! && urlIntegrationQuery.data.auth_url
                }
              >
                <Button
                  className={classes.cardButton}
                  loading={
                    urlIntegrationQuery.isFetching ||
                    userIntegrationsQuery.isFetching
                  }
                >
                  Habilitar
                </Button>
              </Link>
            )}
          </Box>
        ))}
      </Box>

      <AlertDialog {...getModalProps()} />
    </Layout>
  )
}
